import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useFeatureValue } from '@growthbook/growthbook-react'

import { selectCurrentVariantCohort } from 'root-redux/selects/common'

import { useABTest } from 'hooks/useABTest'
import { useActiveCohortIdentifier } from 'hooks/useActiveCohortIdentifier'
import { useCohortToUse } from 'hooks/useCohortToUse'
import { useIsCancelOfferRoute } from 'hooks/useIsCancelOfferRoute'

import {
  ABCDTestVariants,
  BillingCycle,
  GROWTHBOOK_EXPERIMENT,
  LUVLY_REF6_CHILD_COHORT,
  SubscriptionProduct,
} from 'root-constants'

import {
  CANCEL_OFFER_ADDITIONAL_DISCOUNT,
  Currency,
  DECORATING_DISCOUNT,
  DEFAULT_DYNAMIC_DISCOUNT,
  ONE_HUNDRED_PERCENTAGE,
  SUBSCRIPTION_DISCOUNTS,
} from '../constants'
import { getDynamicCohortDiscountPercentage } from '../helpers/getDynamicCohortDiscountPercentage'
import {
  selectDynamicDiscount,
  selectSubscription,
} from '../redux/selects/common'
import { usePriceWithVatAdjustment } from './usePriceWithVatAdjustment'
import { usePurchaseStore } from './usePurchaseStore'

const INITIAL_VALUE = 0

export const useSummarizedSubscriptionData = () => {
  const selectedSubscription = useSelector(selectSubscription)
  const dynamicDiscount = useSelector(selectDynamicDiscount)
  const cohort = useSelector(selectCurrentVariantCohort) as string

  const cohortToUse = useCohortToUse()
  const isCancelOfferRoute = useIsCancelOfferRoute()
  const { isLuvlyIntroFastNewPaywallActive } = useABTest()
  const { isDynamicDiscountCohort, isLuvlyIntroFastCohort } =
    useActiveCohortIdentifier()

  const refNewPricingVariant = useFeatureValue(
    GROWTHBOOK_EXPERIMENT.LV_1530,
    ABCDTestVariants.VARIANT_A,
  )

  const { currency, discountAmount, periodQuantity } = usePurchaseStore()
  const { getPriceWithVatAdjustment } = usePriceWithVatAdjustment()

  const isNoTrialSubscription = useMemo(() => {
    return (
      cohort === LUVLY_REF6_CHILD_COHORT ||
      (refNewPricingVariant === ABCDTestVariants.VARIANT_C &&
        periodQuantity === BillingCycle.ONE_MONTH)
    )
  }, [cohort, refNewPricingVariant, periodQuantity])

  if (!selectedSubscription) {
    return {
      discountPercentage: INITIAL_VALUE,
      totalAmount: INITIAL_VALUE,
      discountAmount: INITIAL_VALUE,
    }
  }

  if (isNoTrialSubscription) {
    const defaultDiscountPercentage = isCancelOfferRoute
      ? SUBSCRIPTION_DISCOUNTS.INTRO_SALE + CANCEL_OFFER_ADDITIONAL_DISCOUNT
      : SUBSCRIPTION_DISCOUNTS.INTRO_SALE

    const lifetimeTotalAmount = getPriceWithVatAdjustment(
      selectedSubscription.mainPrices.oldPrices.fullPrice,
    )

    return {
      discountPercentage:
        selectedSubscription.mainPrices.oldPrices.percentOfDiscount ||
        defaultDiscountPercentage,
      totalAmount: lifetimeTotalAmount,
      discountAmount: Number(
        (
          lifetimeTotalAmount - selectedSubscription.mainPrices.fullPrice
        ).toFixed(2),
      ),
    }
  }

  const getNormalizedPrice = (discount: number) => {
    const calculatedOldPrice =
      selectedSubscription.trialPrices.fullPrice /
      ((ONE_HUNDRED_PERCENTAGE - discount) / ONE_HUNDRED_PERCENTAGE)

    if (currency === Currency.JPY) {
      return Math.ceil(getPriceWithVatAdjustment(calculatedOldPrice))
    }

    return (
      Math.ceil(getPriceWithVatAdjustment(calculatedOldPrice)) -
      DECORATING_DISCOUNT
    )
  }

  if (isDynamicDiscountCohort) {
    const appliedDiscount = getDynamicCohortDiscountPercentage({
      cohort: cohortToUse,
      isCancelOfferRoute,
      dynamicDiscountAmount: dynamicDiscount?.amount,
    })

    const totalAmount = getNormalizedPrice(appliedDiscount)

    return {
      discountPercentage: appliedDiscount,
      totalAmount,
      discountAmount: Number(
        (totalAmount - selectedSubscription.trialPrices.fullPrice).toFixed(2),
      ),
    }
  }

  const totalAmount =
    selectedSubscription.trialPrices.durationDays ===
    SubscriptionProduct.SEVEN_DAY
      ? selectedSubscription.trialPrices.oldPrices.fullPrice
      : selectedSubscription.mainPrices.fullPrice

  const defaultDiscountPercentage = isCancelOfferRoute
    ? DEFAULT_DYNAMIC_DISCOUNT + CANCEL_OFFER_ADDITIONAL_DISCOUNT
    : DEFAULT_DYNAMIC_DISCOUNT

  if (
    isLuvlyIntroFastCohort &&
    isLuvlyIntroFastNewPaywallActive &&
    selectedSubscription.trialPrices.durationDays ===
      SubscriptionProduct.SEVEN_DAY
  ) {
    return {
      discountPercentage:
        selectedSubscription.mainPrices.oldPrices.percentOfDiscount ||
        defaultDiscountPercentage,
      totalAmount: getPriceWithVatAdjustment(
        selectedSubscription.mainPrices.fullPrice,
      ),
      discountAmount: Number(
        (
          getPriceWithVatAdjustment(selectedSubscription.mainPrices.fullPrice) -
          selectedSubscription.trialPrices.fullPrice
        ).toFixed(2),
      ),
    }
  }

  return {
    discountPercentage:
      selectedSubscription.mainPrices.oldPrices.percentOfDiscount ||
      defaultDiscountPercentage,
    totalAmount: getPriceWithVatAdjustment(totalAmount),
    discountAmount,
  }
}
